import React, { useState, useEffect } from "react";
import { Bar, Line, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import { UserIcon, ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { decryptData } from "../../utils/storageCrypto";
ChartJS.register(...registerables);

const QuotationDashboard = () => {
  const [activities, setActivities] = useState([]);
  console.log(activities);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('quotation_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterRole, setFilterRole] = useState('');
  const navigate = useNavigate();

  const getAllQuotations = process.env.REACT_APP_FORE_BRICKBUCKET + `customer/quotations/get-all-quotations?hubId=${sessionStorage.getItem('hubId')}`;

  useEffect(() => {
    const fetchAllQuotations = async () => {
        const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);

        try {
          const response = await fetch(getAllQuotations, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-token': token,
            },
          });
      
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
      
          const data = await response.json();
          console.log('Response structure:', data);
          
          const quotationsArray = data.quotations || data.data || [];
          console.log('Quotations array:', quotationsArray);
          const draftQuotations = quotationsArray.filter(quotation => quotation.ai_generated === true && quotation.status === 'Draft');
          
          console.log('Draft quotations:', draftQuotations);
          setActivities(draftQuotations);
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
      };

    fetchAllQuotations();
  }, []);

  if (loading) return <div className="p-4 text-center"><div>Loading quotations...</div></div>;
  if (error) return <div className="p-4 text-center text-red-500"><div>Error: {error}</div></div>;

  const formatDateTime = (isoDate) => {
    const dateObj = new Date(isoDate);
    const date = dateObj.toLocaleDateString();
    const time = dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
    return `${date} ${time}`;
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedActivities = activities
    .filter(activity => 
      activity?.customer_id?.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity?.customer_id?.mobile_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity?.quotation_number?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aValue, bValue;
      
      // Handle nested object properties
      if (sortField === 'customer_id.customer_name') {
        aValue = a?.customer_id?.customer_name || '';
        bValue = b?.customer_id?.customer_name || '';
      } else if (sortField === 'customer_id.mobile_number') {
        aValue = a?.customer_id?.mobile_number || '';
        bValue = b?.customer_id?.mobile_number || '';
      } else {
        aValue = a[sortField] || '';
        bValue = b[sortField] || '';
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastItem = currentPage * itemsPerPage;  
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedActivities.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAndSortedActivities.length / itemsPerPage);

  return (
    <div>
      {currentItems.length > 0 ? (
        (sessionStorage.getItem('roleId') === '1' || sessionStorage.getItem('roleId') === '0') && (
          <div>
            <div className="relative">
              <div className="absolute inset-0 bg-[#F9F9FB] rounded-t-lg"></div>
              <div className="relative flex justify-between items-center p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-800">AI Generated Quotations</h2>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm text-blue-500 disabled:text-gray-300 hover:bg-blue-50 rounded transition-colors"
                  >
                    &lt;
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm text-blue-500 disabled:text-gray-300 hover:bg-blue-50 rounded transition-colors"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-2 sm:space-y-0">
                <input
                  type="text"
                  placeholder="Search by customer name, mobile, or quotation number..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page when searching
                  }}
                  className="w-full sm:flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="text-sm text-gray-600 mb-4">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredAndSortedActivities.length)} of {filteredAndSortedActivities.length} quotations
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-center border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('quotation_date')}>
                        Date {sortField === 'quotation_date' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('customer_id.customer_name')}>
                        Customer Name {sortField === 'customer_id.customer_name' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('customer_id.mobile_number')}>
                        Mobile Number {sortField === 'customer_id.mobile_number' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th className="p-3 text-sm border font-semibold cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('quotation_number')}>
                        Quotation {sortField === 'quotation_number' && (sortDirection === 'asc' ? '▲' : '▼')}
                      </th>
                      <th className="p-3 text-sm border font-semibold text-center">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((activity, index) => (
                      <tr key={activity?._id || index} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                        <td className="p-3 text-sm border">{formatDateTime(activity?.quotation_date)}</td>
                        <td className="p-3 text-sm border font-medium">{activity?.customer_id?.customer_name || 'N/A'}</td>
                        <td className="p-3 text-sm border">{activity?.customer_id?.mobile_number || 'N/A'}</td>
                        <td className="p-3 text-sm border">
                          <span 
                            className="text-blue-500 cursor-pointer hover:underline font-medium"
                            onClick={() => navigate(`/admin/app/materialreq2`, { 
                              state: { quotationId: activity._id } 
                            })}
                          >
                            {activity.quotation_number}
                          </span>
                        </td>
                        <td className="p-3 text-sm border">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            activity?.status === 'Draft' ? 'bg-yellow-100 text-yellow-800' :
                            activity?.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                            activity?.status === 'Approved' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {activity?.status || 'Draft'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="p-8 text-center text-gray-500">
          <div className="mb-4">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-300" />
          </div>
          <h3 className="text-lg font-medium mb-2">No AI Generated Quotations Found</h3>
          <p className="text-sm">AI generated quotations will appear here when available.</p>
        </div>
      )}
    </div>
  );
};

export default QuotationDashboard;
